import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import diff from 'diff-arrays-of-objects';
import PrimaryButton from '../PrimaryButton';
import ContentEditor from '../ContentEditor';
import NodeContentTopSection from '../NodeContentTopSection';
import Popup from '../Popup';
import AnnouncePopup from '../AnnouncePopup';
import useApi from '../../hooks/useApi';
import RemoveIcon from '../../../public/assets/images/editor/delete-icon-black.svg';
import CopyNodeIcon from '../../../public/assets/images/icons/copy-to-clipboard-black.svg';
import AnnounceNodeIcon from '../../../public/assets/images/icons/megaphone-dark.svg';
import InfoIconInactive from '../../../public/assets/images/icons/info-green.svg';
import InfoIconActive from '../../../public/assets/images/icons/info-white.svg';
import ChecklistContentEditor from './nodes/ChecklistContentEditor';
import ChunkContentEditorFooter from './nodes/ChunkContentEditorFooter';
import WrittenQuizContentEditor from './nodes/WrittenQuizContentEditor';
import QuizContentEditorFooter from './nodes/QuizContentEditorFooter';
import QuizContentEditor from './nodes/QuizContentEditor';
import EvaluationContentEditor from './nodes/EvaluationContentEditor';
import GlossaryContentEditor from './nodes/GlossaryContentEditor';
import CorrelationContentEditorFooter from './nodes/CorrelationContentEditorFooter';
import {
  debounce,
  setStatusSaving,
  setStatusSaved,
  setStatusSaveFailed,
  getAutoSaveInterval,
  triggerEvent,
} from '../../utils';

import {
  PanelWrapper,
  HeaderSection,
  HeaderText,
  ContentSection,
  FooterSection,
  BottomPanel,
  InfoPanel,
  InfoText,
  InfoIcon,
  DeleteIcon,
  CopyIcon,
  AnnounceIcon,
  Wrapper,
} from './styles';

const NodeContentEditor = (props) => {
  const { nodeId, type: baseType, editor, panel, fileService, flash } = props;
  const { options, useAutoSave, contentEditor } = editor;
  const { updateNode } = contentEditor;
  const { userToken, integrations, circle_id, circle_state } = options;

  const { getData, loading } = useApi(userToken);
  const { postData } = useApi(userToken);
  const titleRef = useRef();

  const [updatedAt, setUpdatedAt] = useState();
  const [isInfoShowing, setIsInfoShowing] = useState(false);
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [data, setData] = useState();
  const [body, setBody] = useState();
  const [payload, setPayload] = useState({});
  const [isUpdating, setIsUpdating] = useState(true);
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [showSaveButton, setShowSaveButton] = useState(!useAutoSave);
  const [confirmPopupIsOpen, setConfirmPopupIsOpen] = useState(false);
  const [announcePopupIsOpen, setAnnouncePopupIsOpen] = useState(false);

  const getType = (type) => {
    switch (type) {
      case 'private_blog':
        return 'logbook';
      case 'public_blog':
        return 'noticeboard';
      case 'legend':
        return 'correlation';
      case 'group':
        return 'group_task';
      default:
        return type;
    }
  };

  const nodeDescription = i18n.__(`${getType(baseType)}_node_description`);
  const placeholderKey = type === 'evaluation' ? 'evaluation_body_placeholder' : 'default_texteditor_placeholder';
  const isArticle = ['chunk', 'video', 'link'].includes(baseType);
  const isSession = circle_state === 'session';

  useEffect(() => {
    const getNodeData = async () => {
      if (!loading) {
        const nodeType = isArticle ? 'chunks' : 'behaviors';
        const response = await getData(`/api/v3/editor/${nodeType}/${nodeId}`);
        if (response.success) {
          const body = response.body;
          const description = isArticle ? body.data.content : body.data.body;
          setTitle(body.name || '');
          setType(body.type || '');
          setDescription(description || '');
          setImage(body.image);
          setUpdatedAt(body.updated_at);
          setData(body.data);
          setBody(body);
          setPayload(getNodeInfo(body.name, description, body.type));
        }
      }
    };
    getNodeData();
  }, []);

  useEffect(() => {
    if (data) {
      setIsUpdating(false);
    }
  }, [data]);

  useEffect(() => {
    if (!loading && !title) {
      titleRef?.current?.focus();
    }
  }, [loading, title]);

  useEffect(() => {
    document.addEventListener('nodeContentGenerated', handleGeneratedContent);
    return () => document.removeEventListener('nodeContentGenerated', handleGeneratedContent);
  }, []);

  useEffect(() => {
    document.addEventListener('useAutoSave', handleAutoSave);
    return () => document.removeEventListener('useAutoSave', handleAutoSave);
  }, []);

  const setupAutogrow = () => {
    const element = document.querySelector('.autogrow');
    if (element) {
      setTimeout(() => {
        window.Autogrow(element);
      }, 100);
    }
  };

  useEffect(() => {
    if (!loading) {
      setupAutogrow();
    }
  }, [loading]);

  useEffect(() => {
    document.addEventListener('toggleDyslexicFont', setupAutogrow);
    return () => document.removeEventListener('toggleDyslexicFont', setupAutogrow);
  }, []);

  const handleGeneratedContent = (ev) => {
    const { title, article } = ev.detail || {};
    setTitle(title);
    setDescription(article);
  };

  const handleAutoSave = (ev) => {
    setShowSaveButton(!ev.detail.useAutoSave);
  };

  const toggleConfirmPopup = () => {
    setConfirmPopupIsOpen(!confirmPopupIsOpen);
  };

  const toggleAnnouncePopup = () => {
    setAnnouncePopupIsOpen(!announcePopupIsOpen);
  };

  const debouncedSave = useCallback(
    debounce(async (item) => {
      await save(item);
    }, getAutoSaveInterval()),
    [],
  );

  useEffect(() => {
    if (useAutoSave && updatedAt && !loading && payload?.immediateSave !== undefined) {
      setStatusSaving();
      if (payload?.immediateSave && !isUpdating) {
        save({ payload, updatedAt });
      } else {
        setIsUpdating(true);
        debouncedSave({ payload, updatedAt });
      }
    }
  }, [JSON.stringify(payload)]);

  const getNodeInfo = (title = '', description = '', type = '') => {
    return {
      node_information: {
        name: title,
        type,
      },
      [isArticle ? 'content' : 'body']: description,
    };
  };

  useEffect(() => {
    if (isDirty(title, description, type)) {
      setPayload({
        ...payload,
        ...getNodeInfo(title, description, type),
        immediateSave: false,
      });
    }
  }, [title, description, type]);

  const onChangeTitle = useCallback((value) => {
    setTitle(value);
  }, []);

  const onChangeDescription = useCallback((value) => {
    setDescription(value);
  }, []);

  const toggleInfo = () => {
    setIsInfoShowing(!isInfoShowing);
  };

  const remove = () => {
    toggleConfirmPopup();
    triggerEvent('deleteNode', { nodeId });
    panel.hide();
  };

  const save = async ({ payload, updatedAt }) => {
    const nodeData = {
      ...payload,
      updated_at: updatedAt,
    };
    const nodeType = isArticle ? 'chunks' : 'behaviors';
    const response = await postData(`/api/v3/editor/${nodeType}/${nodeId}`, nodeData);
    if (response.success) {
      setStatusSaved();
      setIsUpdating(false);
      const body = response.body;
      setUpdatedAt(body.updated_at);
      if (body.data) {
        setData(body.data);
      }
      updateNode({ ...payload, uuid: nodeId });
    } else {
      setStatusSaveFailed(i18n.__('behavior_save_failed'));
      setIsUpdating(false);
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__('error_edit_conflict_behavior'), { ttl: 5000 });
      } else if (response.message === 'payload_too_large') {
        flash(i18n.__('error_payload_too_large'), { ttl: 20000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  const copyNode = () => {
    const imageName = image ? image.split('/').reverse()[0] : '';
    triggerEvent('copyToClipboard', { uuid: nodeId, name: title, imageName, type, copiedAt: Date.now() });
  };

  const isDirty = (title, description, type) => {
    if (!body) {
      return false;
    } else {
      const isNotEqual = (a, b) => (a || '') !== (b || '');
      return (
        isNotEqual(title, body.name) ||
        isNotEqual(description, isArticle ? data.content : data.body) ||
        isNotEqual(type, body.type)
      );
    }
  };

  const getUpdates = (oldItems, newItems) => {
    const { added, removed, updated } = diff(oldItems, newItems, 'id');
    const itemsAddedOrRemoved = added.length > 0 || removed.length > 0;
    const itemsUpdated = updated.length > 0;
    return { itemsAddedOrRemoved, itemsUpdated, deletedIds: removed.map((item) => item.id), updated };
  };

  if (!data) {
    return null;
  }

  const topSection = <NodeContentTopSection userToken={userToken} nodeType={type} panel={panel} />;

  const headerSection = (
    <HeaderSection>
      <HeaderText
        ref={titleRef}
        rows="1"
        aria-label={i18n.__('node_name_placeholder')}
        placeholder={i18n.__('node_name_placeholder')}
        value={title}
        onChange={(event) => !loading && onChangeTitle(event.target.value)}
        className="autogrow"
      />
    </HeaderSection>
  );

  const commonContent = (
    <ContentEditor
      type={type}
      aria-label={i18n.__(placeholderKey)}
      placeholder={i18n.__(placeholderKey)}
      value={description}
      onChange={(value) => !loading && onChangeDescription(value)}
      integrations={integrations}
      fileService={fileService}
      canToggleToolbar={false}
      showToolbar={true}
      loading={loading ? loading : undefined}
    />
  );

  const contentSection = () => {
    const getComponent = () => {
      switch (type) {
        case 'checklist':
          return ChecklistContentEditor;
        case 'evaluation':
          return EvaluationContentEditor;
        case 'written_quiz':
          return WrittenQuizContentEditor;
        case 'quiz':
          return QuizContentEditor;
        case 'legend':
        case 'correlation':
          return GlossaryContentEditor;
        default:
          return ContentSection;
      }
    };
    const ContentComponent = getComponent();
    const useData = typeof ContentComponent === 'function';

    return (
      <ContentComponent
        data={useData ? data : null}
        payload={payload}
        setPayload={setPayload}
        type={type}
        fileService={fileService}
        loading={loading ? loading : undefined}
        updating={isUpdating}
        userToken={userToken}
        getUpdates={getUpdates}
      >
        {commonContent}
      </ContentComponent>
    );
  };

  const nodeFooterSection = () => {
    const getComponent = () => {
      switch (type) {
        case 'chunk':
        case 'video':
        case 'link':
          return ChunkContentEditorFooter;
        case 'quiz':
        case 'written_quiz':
          return QuizContentEditorFooter;
        case 'correlation':
          return CorrelationContentEditorFooter;
      }
    };
    const FooterComponent = getComponent();

    return (
      FooterComponent && (
        <FooterComponent
          type={type}
          setType={setType}
          data={data}
          payload={payload}
          setPayload={setPayload}
          loading={loading ? loading : undefined}
          updating={isUpdating}
        />
      )
    );
  };

  const footerSection = (
    <FooterSection>
      {isInfoShowing && (
        <InfoPanel>
          <InfoText>{nodeDescription}</InfoText>
        </InfoPanel>
      )}
      <BottomPanel>
        <Wrapper>
          <DeleteIcon src={RemoveIcon} title={i18n.__('confirm_node_delete.title')} onClick={toggleConfirmPopup}>
            {i18n.__('app_delete')}
          </DeleteIcon>
          <CopyIcon src={CopyNodeIcon} title={i18n.__('app_copy_to_clipboard')} onClick={copyNode}>
            {i18n.__('app_copy')}
          </CopyIcon>
          {isSession && (
            <AnnounceIcon
              src={AnnounceNodeIcon}
              title={i18n.__('app_announce_explaination')}
              onClick={toggleAnnouncePopup}
            >
              {i18n.__('app_announce')}
            </AnnounceIcon>
          )}
        </Wrapper>
        <InfoIcon
          src={isInfoShowing ? InfoIconActive : InfoIconInactive}
          onClick={toggleInfo}
          title={i18n.__('app_node_information')}
        />
        <Wrapper>
          {nodeFooterSection()}
          {showSaveButton && (
            <PrimaryButton
              onClick={() => {
                setStatusSaving();
                save({ payload, updatedAt });
              }}
              text={i18n.__('app_save')}
              className="save-button"
            />
          )}
        </Wrapper>
      </BottomPanel>
    </FooterSection>
  );

  const confirmPopup = (
    <Popup
      popupIsOpen={confirmPopupIsOpen}
      id="confirmPopup"
      heading={i18n.__('confirm_node_delete.title')}
      description={i18n.__('confirm_node_delete.body', { name: title })}
      buttonLabel={i18n.__('confirm_node_delete.action')}
      onClose={() => setConfirmPopupIsOpen(false)}
      onClick={remove}
    />
  );

  const announcePopup = (
    <AnnouncePopup
      userToken={userToken}
      loopId={circle_id}
      nodeId={nodeId}
      subType={'node'}
      popupIsOpen={announcePopupIsOpen}
      onClose={() => setAnnouncePopupIsOpen(false)}
      flash={flash}
    />
  );

  return (
    !loading && (
      <PanelWrapper>
        {topSection}
        {headerSection}
        {contentSection()}
        {footerSection}
        {confirmPopup}
        {announcePopup}
      </PanelWrapper>
    )
  );
};

export default NodeContentEditor;

NodeContentEditor.propTypes = {
  nodeId: PropTypes.string.isRequired,
  editor: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
};
